import React, { useState, useRef, useEffect } from 'react'
import { toJpeg } from 'html-to-image'
import TileSelectionPopup from './TileSelectionPopup'

interface Tile {
  id: string
  name: string
  texturePath: string // ścieżka do tekstury
  tileSize: number // rozmiar kwadratu w pikselach
}

// Helper do kodowania ścieżek tekstur (koduje spacje i specjalne znaki)
function encodeTexturePath(path: string): string {
  const parts = path.split('/')
  return parts.map(part => {
    if (part && part !== '') {
      return encodeURIComponent(part)
    }
    return part
  }).join('/')
}

interface Surface {
  id: string
  name: string
  points: { x: number; y: number }[]
  tile: Tile | null // wybrana tekstura
}

interface PerspectivePoints {
  topLeft: { x: number; y: number }
  topRight: { x: number; y: number }
  bottomLeft: { x: number; y: number }
  bottomRight: { x: number; y: number }
}

const Wizualizator: React.FC = () => {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [surfaces, setSurfaces] = useState<Surface[]>([])
  const [selectedSurface, setSelectedSurface] = useState<string | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [currentPoints, setCurrentPoints] = useState<{ x: number; y: number }[]>([])
  const [showTilePopup, setShowTilePopup] = useState(false)
  const [perspectiveMode] = useState<'overlay' | 'realistic'>('realistic')
  const [perspectivePoints, setPerspectivePoints] = useState<PerspectivePoints | null>(null)
  const [isEditingPerspective, setIsEditingPerspective] = useState(false)
  const [draggedPoint, setDraggedPoint] = useState<string | null>(null)
  const [tileSize, setTileSize] = useState(70) // rozmiar kwadratu w pikselach
  const [tileRotation, setTileRotation] = useState(0) // obrót tekstury w stopniach
  const [showLeftSidebar, setShowLeftSidebar] = useState(true)
  const [showRightSidebar, setShowRightSidebar] = useState(false) // Domyślnie zamknięty na mobile
  const [beforeAfterSlider, setBeforeAfterSlider] = useState(100) // 🎯 Domyślnie 100% widoczność
  const [snapToGrid, setSnapToGrid] = useState(false)
  const [gridSize, setGridSize] = useState(10)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const visualizationContainerRef = useRef<HTMLDivElement>(null)



  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      processImageFile(file)
    }
  }

  const processImageFile = (file: File) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        setUploadedImage(e.target?.result as string)
      setSurfaces([])
      setSelectedSurface(null)
      setPerspectivePoints(null)
      }
      reader.readAsDataURL(file)
    }

  // Obsługa drag&drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const files = Array.from(e.dataTransfer.files)
    const imageFile = files.find(file => file.type.startsWith('image/'))
    if (imageFile) {
      processImageFile(imageFile)
    }
  }

  // Obsługa aparatu (webcam)
  const handleCameraCapture = () => {
    // Implementacja obsługi aparatu
    alert('Funkcja aparatu będzie dostępna wkrótce!')
  }

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return

    console.log('🖱️ Pojedyncze kliknięcie na canvas')

    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    let x = event.clientX - rect.left
    let y = event.clientY - rect.top

    // Snap do siatki jeśli włączony
    if (snapToGrid) {
      x = Math.round(x / gridSize) * gridSize
      y = Math.round(y / gridSize) * gridSize
    }

    console.log('📍 Pozycja kliknięcia:', x, y)
    console.log('📊 Aktualne punkty:', currentPoints.length)

    // Sprawdź czy kliknięto na pierwszy punkt (zamknięcie obwodu)
    if (currentPoints.length > 0) {
      const firstPoint = currentPoints[0]
      const distance = Math.sqrt((x - firstPoint.x) ** 2 + (y - firstPoint.y) ** 2)
      
      console.log('🎯 Pierwszy punkt:', firstPoint)
      console.log('📏 Odległość od pierwszego punktu:', distance)
      
      if (distance < 30) { // Zwiększona tolerancja dla łatwiejszego kliknięcia
        console.log('✅ Kliknięto na pierwszy punkt - zamykanie obwodu')
        const newSurface: Surface = {
          id: `surface-${Date.now()}`,
          name: `Powierzchnia ${surfaces.length + 1}`,
          points: currentPoints,
          tile: null
        }
        setSurfaces([...surfaces, newSurface])
        
        // 🎯 Automatycznie ustaw punkty perspektywy na rogach nowej powierzchni
        if (currentPoints.length >= 4) {
          setPerspectivePoints({
            topLeft: currentPoints[0],
            topRight: currentPoints[1],
            bottomRight: currentPoints[2],
            bottomLeft: currentPoints[3]
          })
          setIsEditingPerspective(true) // Automatycznie pokaż punkty
        }
        
        setCurrentPoints([])
        setIsDrawing(false)
        return
      }
    }

    console.log('➕ Dodawanie nowego punktu')
    const newPoints = [...currentPoints, { x, y }]
    setCurrentPoints(newPoints)

    // Nie kończ automatycznie - pozwól użytkownikowi zdecydować
  }

  const handleCanvasDoubleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    console.log('🖱️🖱️ Podwójne kliknięcie na canvas')
    
    if (!isDrawing) {
      console.log('❌ Nie rysujemy - ignorowanie podwójnego kliknięcia')
      return
    }
    
    if (currentPoints.length < 3) {
      console.log('❌ Za mało punktów:', currentPoints.length, '- potrzebne minimum 3')
      return
    }

    console.log('✅ Podwójne kliknięcie - zamykanie wielokąta z', currentPoints.length, 'punktami')
    
    // Podwójne kliknięcie kończy wielokąt
    const newSurface: Surface = {
      id: `surface-${Date.now()}`,
      name: `Powierzchnia ${surfaces.length + 1}`,
      points: currentPoints,
      tile: null
    }
    setSurfaces([...surfaces, newSurface])
    
    // 🎯 Automatycznie ustaw punkty perspektywy na rogach nowej powierzchni
    if (currentPoints.length >= 4) {
      setPerspectivePoints({
        topLeft: currentPoints[0],
        topRight: currentPoints[1],
        bottomRight: currentPoints[2],
        bottomLeft: currentPoints[3]
      })
      setIsEditingPerspective(true) // Automatycznie pokaż punkty
    }
    
    setCurrentPoints([])
    setIsDrawing(false)
    
    // Zapobiegaj propagacji do pojedynczego kliknięcia
    event.preventDefault()
    event.stopPropagation()
  }


  // Cofanie zmian
  const undoLastAction = () => {
    if (currentPoints.length > 0) {
      setCurrentPoints(currentPoints.slice(0, -1))
    } else if (surfaces.length > 0) {
      setSurfaces(surfaces.slice(0, -1))
    }
  }

  const startDrawingSurface = () => {
    setIsDrawing(true)
    setCurrentPoints([])
  }

  const cancelDrawing = () => {
    setIsDrawing(false)
    setCurrentPoints([])
  }

  const addTileToSurface = (surfaceId: string, tile: Tile) => {
    console.log('🎯 Dodawanie tekstury do powierzchni:', surfaceId, 'tekstura:', tile.name)
    
    setSurfaces(prevSurfaces => {
      return prevSurfaces.map(s => 
        s.id === surfaceId 
          ? { ...s, tile: tile }
          : s
      )
    })

    console.log('✅ Tekstura dodana do powierzchni:', surfaceId)
  }

  const handleTileSelect = (tile: Tile) => {
    if (selectedSurface) {
      addTileToSurface(selectedSurface, tile)
    }
  }

  // 🎯 Funkcja pomocnicza do obliczania CSS matrix3d z 4 punktów perspektywy
  const getPerspectiveTransform = (
    surfacePoints: { x: number; y: number }[],
    perspPoints: PerspectivePoints | null
  ): string => {
    if (!perspPoints || surfacePoints.length < 4) {
      return 'none'
    }

    // Użyj punktów perspektywy do obliczenia transformacji
    // Dla uproszczenia, użyjemy CSS clip-path i podstawową transformację
    const centerX = (perspPoints.topLeft.x + perspPoints.topRight.x + perspPoints.bottomLeft.x + perspPoints.bottomRight.x) / 4
    const centerY = (perspPoints.topLeft.y + perspPoints.topRight.y + perspPoints.bottomLeft.y + perspPoints.bottomRight.y) / 4

    // Oblicz kąty rotacji na podstawie punktów perspektywy
    const topWidth = perspPoints.topRight.x - perspPoints.topLeft.x
    const bottomWidth = perspPoints.bottomRight.x - perspPoints.bottomLeft.x
    const leftHeight = perspPoints.bottomLeft.y - perspPoints.topLeft.y
    const rightHeight = perspPoints.bottomRight.y - perspPoints.topRight.y

    // Oblicz rotację Y (perspektywa w poziomie)
    const rotateY = Math.atan((bottomWidth - topWidth) / (leftHeight + rightHeight)) * (180 / Math.PI) * 0.5

    // Oblicz rotację X (perspektywa w pionie)
    const rotateX = Math.atan((rightHeight - leftHeight) / (topWidth + bottomWidth)) * (180 / Math.PI) * 0.5

    return `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`
  }

  // 🎯 Funkcja do rysowania tekstury w perspektywie
  const drawTextureInPerspective = (
    canvas: HTMLCanvasElement,
    textureImage: HTMLImageElement,
    surfacePoints: { x: number; y: number }[],
    perspPoints: PerspectivePoints,
    tileSize: number,
    rotation: number
  ) => {
    const ctx = canvas.getContext('2d')
    if (!ctx || surfacePoints.length < 3) return

    // Wyczyść canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Utwórz clipPath z punktów powierzchni
    ctx.save()
    ctx.beginPath()
    ctx.moveTo(surfacePoints[0].x, surfacePoints[0].y)
    for (let i = 1; i < surfacePoints.length; i++) {
      ctx.lineTo(surfacePoints[i].x, surfacePoints[i].y)
    }
    ctx.closePath()
    ctx.clip()

    // Oblicz bounding box powierzchni
    const minX = Math.min(...surfacePoints.map(p => p.x))
    const maxX = Math.max(...surfacePoints.map(p => p.x))
    const minY = Math.min(...surfacePoints.map(p => p.y))
    const maxY = Math.max(...surfacePoints.map(p => p.y))

    // Użyj punktów perspektywy do transformacji
    const srcPoints = [
      { x: 0, y: 0 },
      { x: textureImage.width, y: 0 },
      { x: textureImage.width, y: textureImage.height },
      { x: 0, y: textureImage.height }
    ]

    // Punkty docelowe (z perspektywy)
    const dstPoints = [
      perspPoints.topLeft,
      perspPoints.topRight,
      perspPoints.bottomRight,
      perspPoints.bottomLeft
    ]

    // Oblicz ile kafelków potrzebujemy
    const tilesX = Math.ceil((maxX - minX) / tileSize)
    const tilesY = Math.ceil((maxY - minY) / tileSize)

    // Rysuj kafelki tekstury w pętli
    for (let ty = 0; ty < tilesY; ty++) {
      for (let tx = 0; tx < tilesX; tx++) {
        const destX = minX + tx * tileSize
        const destY = minY + ty * tileSize

        ctx.save()
        ctx.translate(destX + tileSize / 2, destY + tileSize / 2)
        ctx.rotate((rotation * Math.PI) / 180)
        
        // Zastosuj prostą transformację perspektywy przez skalowanie
        const scaleX = 1 + (destX - minX) / (maxX - minX) * 0.2
        const scaleY = 1 + (destY - minY) / (maxY - minY) * 0.2
        ctx.scale(scaleX, scaleY)
        
        ctx.drawImage(
          textureImage,
          -tileSize / 2,
          -tileSize / 2,
          tileSize,
          tileSize
        )
        ctx.restore()
      }
    }

    ctx.restore()
  }


  const removeSurface = (surfaceId: string) => {
    setSurfaces(surfaces.filter(surface => surface.id !== surfaceId))
    if (selectedSurface === surfaceId) {
      setSelectedSurface(null)
    }
  }

  // Usuwanie tekstury z powierzchni
  const clearSurfaceTexture = (surfaceId: string) => {
    setSurfaces(surfaces.map(surface => 
      surface.id === surfaceId 
        ? { ...surface, tile: null }
        : surface
    ))
  }


  // Obsługa perspektywy
  const initializePerspective = () => {
    if (!uploadedImage) return
    
    const canvas = canvasRef.current
    if (!canvas) return

    // Użyj rzeczywistych wymiarów canvas na ekranie
    const rect = canvas.getBoundingClientRect()
    const canvasWidth = rect.width
    const canvasHeight = rect.height

    // Ustaw domyślne punkty perspektywy z bardziej wyraźną perspektywą
    const marginX = canvasWidth * 0.2 // 20% marginesu poziomego (więcej miejsca na perspektywę)
    const marginY = canvasHeight * 0.15 // 15% marginesu pionowego
    
    // Tworzymy perspektywę "patrzenia w dół" - górne punkty bliżej siebie, dolne dalej
    const perspectiveOffset = canvasWidth * 0.15 // 15% przesunięcia dla perspektywy (większa perspektywa)
    
    setPerspectivePoints({
      topLeft: { x: marginX, y: marginY },
      topRight: { x: canvasWidth - marginX, y: marginY },
      bottomLeft: { x: marginX - perspectiveOffset, y: canvasHeight - marginY }, // przesunięcie w lewo
      bottomRight: { x: canvasWidth - marginX + perspectiveOffset, y: canvasHeight - marginY } // przesunięcie w prawo
    })
    
    // NIE włączaj automatycznie trybu edycji - punkty pokażą się dopiero po narysowaniu powierzchni
  }

  // Automatyczna inicjalizacja perspektywy po załadowaniu obrazu
  useEffect(() => {
    if (uploadedImage && !perspectivePoints) {
      // Małe opóźnienie żeby canvas się załadował
      setTimeout(() => {
        initializePerspective()
      }, 100)
    }
  }, [uploadedImage])

  // Oblicz kąty obrotu perspektywy na podstawie punktów - AUTOMATYCZNIE!
  const calculatePerspectiveRotation = () => {
    if (!perspectivePoints) return { x: 0, y: 0, skewX: 0, skewY: 0 }

    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0, skewX: 0, skewY: 0 }

    // Oblicz różnice między punktami dla pełnej perspektywy
    const topWidth = perspectivePoints.topRight.x - perspectivePoints.topLeft.x
    const bottomWidth = perspectivePoints.bottomRight.x - perspectivePoints.bottomLeft.x
    const leftHeight = perspectivePoints.bottomLeft.y - perspectivePoints.topLeft.y
    const rightHeight = perspectivePoints.bottomRight.y - perspectivePoints.topRight.y

    // Użyj rzeczywistych wymiarów canvas na ekranie
    const rect = canvas.getBoundingClientRect()
    const canvasWidth = rect.width
    const canvasHeight = rect.height
    
    // Oblicz kąty perspektywy - ZWIĘKSZONA czułość dla lepszego efektu!
    const widthDiff = (bottomWidth - topWidth) / canvasWidth
    const heightDiff = (rightHeight - leftHeight) / canvasHeight
    
    // Maksymalna czułość dla naturalnej perspektywy
    const rotateX = -heightDiff * 80 // Rotacja X (góra-dół)
    const rotateY = widthDiff * 80 // Rotacja Y (lewo-prawo)
    
    // Oblicz skew dla dodatkowej głębi perspektywy
    const skewX = widthDiff * 20
    const skewY = heightDiff * 20

    return {
      x: Math.max(-75, Math.min(75, rotateX)),
      y: Math.max(-75, Math.min(75, rotateY)),
      skewX: Math.max(-15, Math.min(15, skewX)),
      skewY: Math.max(-15, Math.min(15, skewY))
    }
  }

  // Oblicz dynamiczny rozmiar kontenera tekstury na podstawie perspektywy
  const calculateTextureContainerSize = (surfaceWidth: number, surfaceHeight: number) => {
    const rotation = calculatePerspectiveRotation()
    const maxRotation = Math.max(Math.abs(rotation.x), Math.abs(rotation.y))
    
    // Oblicz przekątną powierzchni (najdłuższy wymiar)
    const diagonal = Math.sqrt(surfaceWidth * surfaceWidth + surfaceHeight * surfaceHeight)
    
    // Bazowy rozmiar to przekątna + margines bezpieczeństwa (50% większy)
    const baseSize = diagonal * 1.5
    
    // Przy obrotach zwiększamy rozmiar jeszcze bardziej
    const rotationMultiplier = 1 + (maxRotation / 60) * 2 // do 3x większy przy skrajnej perspektywie
    const dynamicSize = baseSize * rotationMultiplier
    
    // Dodatkowy margines dla obrotów tekstury
    const rotationMargin = Math.abs(tileRotation) > 0 ? Math.sqrt(2) : 1
    const finalSize = dynamicSize * rotationMargin
    
    return {
      size: finalSize,
      offset: (finalSize - Math.max(surfaceWidth, surfaceHeight)) / 2
    }
  }

  // Obsługa przeciągania punktów perspektywy
  const handlePerspectivePointMouseDown = (pointName: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDraggedPoint(pointName)
    
    // Dodaj wizualny feedback
    const target = e.currentTarget as HTMLElement
    target.style.transform = 'translate(-1.5px, -1.5px) scale(2)' // Większe powiększenie dla lepszej widoczności
    target.style.zIndex = '1000'
    target.style.backgroundColor = '#3B82F6' // Niebieski (aktywny)
    target.style.boxShadow = '0 0 0 2px white, 0 0 10px rgba(59, 130, 246, 0.5)' // Świecenie
  }

  const resetPerspectivePoints = () => {
    setDraggedPoint(null)
    
    // Przywróć normalny wygląd wszystkich punktów
    const points = document.querySelectorAll('[data-perspective-point]')
    points.forEach(point => {
      const element = point as HTMLElement
      element.style.transform = 'translate(-1.5px, -1.5px) scale(1)'
      element.style.zIndex = '10'
      element.style.backgroundColor = 'white' // Biały
      element.style.boxShadow = '' // Usuń świecenie
    })
  }

  // Dodaj globalne nasłuchiwanie myszy dla lepszego przeciągania
  useEffect(() => {
    let rafId: number | null = null

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!draggedPoint || !perspectivePoints) return

      // Użyj requestAnimationFrame dla płynniejszego przeciągania
      if (rafId) {
        cancelAnimationFrame(rafId)
      }

      rafId = requestAnimationFrame(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const rect = canvas.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top

        // Mniejszy margines dla lepszej kontroli
        const margin = 5
        const clampedX = Math.max(margin, Math.min(rect.width - margin, x))
        const clampedY = Math.max(margin, Math.min(rect.height - margin, y))

        setPerspectivePoints(prev => {
          if (!prev) return null
          return {
            ...prev,
            [draggedPoint]: { x: clampedX, y: clampedY }
          }
        })
      })
    }

    const handleGlobalMouseUp = () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
        rafId = null
      }
      resetPerspectivePoints()
    }

    if (draggedPoint) {
      document.addEventListener('mousemove', handleGlobalMouseMove)
      document.addEventListener('mouseup', handleGlobalMouseUp)
      document.addEventListener('mouseleave', handleGlobalMouseUp)
      
      // Ustaw kursor dla całego dokumentu
      document.body.style.cursor = 'move'
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      document.removeEventListener('mousemove', handleGlobalMouseMove)
      document.removeEventListener('mouseup', handleGlobalMouseUp)
      document.removeEventListener('mouseleave', handleGlobalMouseUp)
      document.body.style.cursor = ''
    }
  }, [draggedPoint, perspectivePoints])







  // Funkcja pomocnicza do renderowania zawartości wizualizacji
  const renderVisualizationContent = () => {
    // Tryb mega-realistyczny używa tej samej logiki co realistyczny, ale z dodatkowymi efektami

    return (
      <div className="max-w-6xl w-full">
        <div className="bg-white rounded-lg shadow-lg p-3 sm:p-4 md:p-6">
          {/* Image Upload Section - tylko na mobile */}
          <div className="mb-4 sm:mb-6 md:hidden">
            <h3 className="text-sm sm:text-base font-semibold mb-3" style={{ color: '#3E3E3F' }}>Zdjęcie działki/domu</h3>
            
            <div 
              className="border-2 border-dashed rounded-xl p-3 sm:p-4 text-center transition-all duration-200"
              style={{ 
                borderColor: '#BFBFBF',
                backgroundColor: uploadedImage ? '#EDEDED' : '#FFFFFF'
              }}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => !uploadedImage && fileInputRef.current?.click()}
            >
              {uploadedImage ? (
                <div className="space-y-3 sm:space-y-4">
                  <div className="relative">
                    <img 
                      src={uploadedImage} 
                      alt="Uploaded property" 
                      className="w-full h-32 sm:h-40 object-cover rounded-lg shadow-sm"
                    />
                    <div className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-sm">
                      <svg className="w-4 h-4" style={{ color: '#1FA650' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        fileInputRef.current?.click()
                      }}
                      className="flex-1 px-3 sm:px-4 py-2 text-sm sm:text-base text-white rounded-lg transition-colors font-medium touch-manipulation mobile-button"
                      style={{ backgroundColor: '#BE2019' }}
                    >
                      Zmień zdjęcie
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setUploadedImage(null)
                        setSurfaces([])
                        setSelectedSurface(null)
                        setPerspectivePoints(null)
                      }}
                      className="flex-1 px-3 sm:px-4 py-2 text-sm sm:text-base rounded-lg transition-colors font-medium touch-manipulation mobile-button"
                      style={{ 
                        backgroundColor: '#CE312A',
                        color: '#FFFFFF'
                      }}
                    >
                      Usuń zdjęcie
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full mx-auto flex items-center justify-center">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-700">Przeciągnij zdjęcie lub kliknij aby wybrać</p>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG do 10MB</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        fileInputRef.current?.click()
                      }}
                      className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-white rounded-lg transition-colors font-medium touch-manipulation mobile-button"
                      style={{ backgroundColor: '#BE2019' }}
                    >
                      Wybierz z galerii
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleCameraCapture()
                      }}
                      className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base rounded-lg transition-colors font-medium touch-manipulation mobile-button"
                      style={{ 
                        backgroundColor: '#EDEDED',
                        color: '#3E3E3F'
                      }}
                    >
                      Zrób zdjęcie aparatem
                    </button>
                  </div>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          </div>
          
          {/* Canvas Section - tylko gdy jest zdjęcie */}
          {uploadedImage && (
            <>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-4 gap-2 sm:gap-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Projekt kostki brukowej</h3>
            <div className="flex flex-wrap gap-2 sm:space-x-2">
              <span className="text-xs sm:text-sm text-gray-600">
                {perspectiveMode === 'overlay' ? 'Nakładka' : 'Rzeczywisty'}
              </span>
              <span className="text-xs sm:text-sm text-gray-600">
                {beforeAfterSlider}% zaznaczone powierzchnie
              </span>
            </div>
          </div>
          
          <div className="relative overflow-hidden" ref={visualizationContainerRef}>
            <div className="relative overflow-hidden">
              <canvas
                ref={canvasRef}
                width={800}
                height={600}
                className="w-full h-[400px] sm:h-[500px] md:h-[600px] border border-gray-300 rounded-lg cursor-crosshair"
                onClick={handleCanvasClick}
                onDoubleClick={handleCanvasDoubleClick}
                        style={{
                          backgroundImage: `url(${uploadedImage})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundColor: '#f8f9fa', // jasnoszary kolor podłogi
                          filter: 'none',
                          boxShadow: 'none'
                        }}
              />
              
              {/* Dodatkowe efekty oświetlenia dla trybu realistycznego */}
              {perspectiveMode === 'realistic' && (
                <>
                  {/* Gradient overlay dla głębi */}
                  <div 
                    className="absolute inset-0 pointer-events-none rounded-lg"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.2) 100%)',
                      mixBlendMode: 'overlay'
                    }}
                  />
                  
                  {/* Efekt oświetlenia z góry */}
                  <div 
                    className="absolute inset-0 pointer-events-none rounded-lg"
                    style={{
                      background: 'radial-gradient(ellipse at top, rgba(255,255,255,0.2) 0%, transparent 70%)',
                      mixBlendMode: 'soft-light'
                    }}
                  />
                  
                  {/* Subtelny vignette */}
                  <div 
                    className="absolute inset-0 pointer-events-none rounded-lg"
                    style={{
                      background: 'radial-gradient(circle at center, transparent 60%, rgba(0,0,0,0.1) 100%)',
                      mixBlendMode: 'multiply'
                    }}
                  />
                </>
              )}
              
            </div>
            
            {/* Grid Overlay */}
            {snapToGrid && (
              <div className="absolute inset-0 pointer-events-none">
                <svg className="w-full h-full">
                  <defs>
                    <pattern id="grid" width={gridSize} height={gridSize} patternUnits="userSpaceOnUse">
                      <path d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`} fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                </svg>
              </div>
            )}
            
            {/* Surface Overlays */}
            {surfaces.map((surface) => {
              const minX = Math.min(...surface.points.map(p => p.x))
              const maxX = Math.max(...surface.points.map(p => p.x))
              const minY = Math.min(...surface.points.map(p => p.y))
              const maxY = Math.max(...surface.points.map(p => p.y))
              
              const surfaceWidth = maxX - minX
              const surfaceHeight = maxY - minY
              const containerSize = calculateTextureContainerSize(surfaceWidth, surfaceHeight)
              
              return (
              <div
                key={surface.id}
                data-surface-id={surface.id}
                className={`absolute ${
                  selectedSurface === surface.id ? 'border-blue-500' : 'border-blue-300'
                } ${perspectiveMode === 'overlay' ? 'bg-blue-100 bg-opacity-50' : 'bg-blue-200 bg-opacity-30'}`}
                style={{
                  left: minX,
                  top: minY,
                  width: surfaceWidth,
                  height: surfaceHeight,
                  clipPath: surface.points.length >= 3 ? `polygon(${surface.points.map(p => `${p.x - minX}px ${p.y - minY}px`).join(', ')})` : 'none',
                  overflow: 'hidden',
                  opacity: beforeAfterSlider / 100,
                }}
              >
                <div className="absolute -top-6 left-0 bg-blue-500 text-white px-2 py-1 rounded text-xs">
                  {surface.name} ({surface.tile ? '1 tekstura' : '0 tekstur'})
                </div>
                
                {/* Simple Texture Overlay */}
                {surface.tile && (
                  <div
                    className="absolute"
                    style={{
                      left: -containerSize.offset,
                      top: -containerSize.offset,
                      width: containerSize.size,
                      height: containerSize.size,
                      transform: perspectivePoints 
                        ? `perspective(800px) rotateX(${calculatePerspectiveRotation().x}deg) rotateY(${calculatePerspectiveRotation().y}deg) skewX(${calculatePerspectiveRotation().skewX}deg) skewY(${calculatePerspectiveRotation().skewY}deg)`
                        : 'none',
                      transformOrigin: 'center center',
                      transformStyle: 'preserve-3d'
                    }}
                  >
                    <div
                      data-texture="true"
                      className="absolute inset-0"
                      style={{
                        backgroundImage: `url(${encodeTexturePath(surface.tile.texturePath)})`,
                        backgroundSize: `${tileSize}px ${tileSize}px`,
                        backgroundPosition: 'center center',
                        backgroundRepeat: 'repeat',
                        opacity: 0.8,
                        transform: `rotate(${tileRotation}deg)`,
                        transformOrigin: 'center center',
                        width: '100%',
                        height: '100%'
                      }}
                    />
                  </div>
                )}
              </div>
              )
            })}
            
            {/* Current Drawing Points */}
            {currentPoints.map((point, index) => (
              <div
                key={index}
                className={`absolute w-4 h-4 rounded-full border-2 border-white transform -translate-x-2 -translate-y-2 ${
                  index === 0 ? 'bg-green-500' : 'bg-red-500'
                }`}
                style={{ left: point.x, top: point.y }}
              />
            ))}
            
            {/* Pierwszy punkt - większy i z podpowiedzią */}
            {currentPoints.length > 0 && (
              <div className="relative">
                <div
                  className="absolute w-8 h-8 bg-green-500 rounded-full border-3 border-white transform -translate-x-4 -translate-y-4 opacity-90 animate-pulse"
                  style={{ left: currentPoints[0].x, top: currentPoints[0].y }}
                />
                <div
                  className="absolute w-12 h-12 bg-green-200 rounded-full border-2 border-green-400 transform -translate-x-6 -translate-y-6 opacity-30 animate-ping"
                  style={{ left: currentPoints[0].x, top: currentPoints[0].y }}
                />
                {/* Tekst podpowiedzi */}
                <div
                  className="absolute bg-green-600 text-white text-xs px-2 py-1 rounded transform -translate-x-1/2 -translate-y-8 whitespace-nowrap"
                  style={{ left: currentPoints[0].x, top: currentPoints[0].y }}
                >
                  Kliknij aby zamknąć
                </div>
              </div>
            )}
            
            {/* Drawing Lines */}
            {currentPoints.length > 1 && (
              <svg className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
                <polyline
                  points={currentPoints.map(p => `${p.x},${p.y}`).join(' ')}
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />
              </svg>
            )}
            
            {/* Perspective Points */}
            {isEditingPerspective && perspectivePoints && (
              <>
                {/* Linie łączące punkty (ramka perspektywy) */}
                <svg className="absolute inset-0 pointer-events-none" style={{ width: '100%', height: '100%' }}>
                  {/* Górna linia */}
                  <line
                    x1={perspectivePoints.topLeft.x}
                    y1={perspectivePoints.topLeft.y}
                    x2={perspectivePoints.topRight.x}
                    y2={perspectivePoints.topRight.y}
                    stroke="#3B82F6"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                  {/* Prawa linia */}
                  <line
                    x1={perspectivePoints.topRight.x}
                    y1={perspectivePoints.topRight.y}
                    x2={perspectivePoints.bottomRight.x}
                    y2={perspectivePoints.bottomRight.y}
                    stroke="#3B82F6"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                  {/* Dolna linia */}
                  <line
                    x1={perspectivePoints.bottomRight.x}
                    y1={perspectivePoints.bottomRight.y}
                    x2={perspectivePoints.bottomLeft.x}
                    y2={perspectivePoints.bottomLeft.y}
                    stroke="#3B82F6"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                  {/* Lewa linia */}
                  <line
                    x1={perspectivePoints.bottomLeft.x}
                    y1={perspectivePoints.bottomLeft.y}
                    x2={perspectivePoints.topLeft.x}
                    y2={perspectivePoints.topLeft.y}
                    stroke="#3B82F6"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                </svg>

                {/* Top Left - kwadrat jak w Photoshopie */}
                <div
                  data-perspective-point="topLeft"
                  className="absolute w-4 h-4 bg-white border-[3px] border-blue-500 transform -translate-x-2 -translate-y-2 cursor-nwse-resize hover:bg-blue-500 hover:scale-150 transition-all duration-150 select-none"
                  style={{ 
                    left: perspectivePoints.topLeft.x, 
                    top: perspectivePoints.topLeft.y,
                    boxShadow: '0 0 0 2px white, 0 2px 8px rgba(0,0,0,0.3), 0 0 20px rgba(59, 130, 246, 0.4)'
                  }}
                  onMouseDown={(e) => handlePerspectivePointMouseDown('topLeft', e)}
                >
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none font-medium shadow-lg">
                    Lewy górny
                  </div>
                </div>

                {/* Top Right */}
                <div
                  data-perspective-point="topRight"
                  className="absolute w-4 h-4 bg-white border-[3px] border-blue-500 transform -translate-x-2 -translate-y-2 cursor-nesw-resize hover:bg-blue-500 hover:scale-150 transition-all duration-150 select-none"
                  style={{ 
                    left: perspectivePoints.topRight.x, 
                    top: perspectivePoints.topRight.y,
                    boxShadow: '0 0 0 2px white, 0 2px 8px rgba(0,0,0,0.3), 0 0 20px rgba(59, 130, 246, 0.4)'
                  }}
                  onMouseDown={(e) => handlePerspectivePointMouseDown('topRight', e)}
                >
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none font-medium shadow-lg">
                    Prawy górny
                  </div>
                </div>

                {/* Bottom Left */}
                <div
                  data-perspective-point="bottomLeft"
                  className="absolute w-4 h-4 bg-white border-[3px] border-blue-500 transform -translate-x-2 -translate-y-2 cursor-nesw-resize hover:bg-blue-500 hover:scale-150 transition-all duration-150 select-none"
                  style={{ 
                    left: perspectivePoints.bottomLeft.x, 
                    top: perspectivePoints.bottomLeft.y,
                    boxShadow: '0 0 0 2px white, 0 2px 8px rgba(0,0,0,0.3), 0 0 20px rgba(59, 130, 246, 0.4)'
                  }}
                  onMouseDown={(e) => handlePerspectivePointMouseDown('bottomLeft', e)}
                >
                  <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none font-medium shadow-lg">
                    Lewy dolny
                  </div>
                </div>

                {/* Bottom Right */}
                <div
                  data-perspective-point="bottomRight"
                  className="absolute w-4 h-4 bg-white border-[3px] border-blue-500 transform -translate-x-2 -translate-y-2 cursor-nwse-resize hover:bg-blue-500 hover:scale-150 transition-all duration-150 select-none"
                  style={{ 
                    left: perspectivePoints.bottomRight.x, 
                    top: perspectivePoints.bottomRight.y,
                    boxShadow: '0 0 0 2px white, 0 2px 8px rgba(0,0,0,0.3), 0 0 20px rgba(59, 130, 246, 0.4)'
                  }}
                  onMouseDown={(e) => handlePerspectivePointMouseDown('bottomRight', e)}
                >
                  <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none font-medium shadow-lg">
                    Prawy dolny
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-0">
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <button
                onClick={async (event) => {
                  const button = event.currentTarget as HTMLButtonElement
                  const originalText = button.textContent
                  button.textContent = 'Generowanie...'
                  button.disabled = true

                  try {
                    const container = visualizationContainerRef.current
                    if (!container) {
                      alert('Brak obszaru do pobrania')
                      return
                    }

                    // Ukryj punkty perspektywy przed screenshotem
                    const perspectivePoints = container.querySelectorAll('[data-perspective-point]')
                    perspectivePoints.forEach(el => (el as HTMLElement).style.display = 'none')

                    // Prosty screenshot z html-to-image (obsługuje clipPath!)
                    const dataUrl = await toJpeg(container, { quality: 0.95, pixelRatio: 2 })

                    // Przywróć punkty perspektywy
                    perspectivePoints.forEach(el => (el as HTMLElement).style.display = '')
                    
                    const link = document.createElement('a')
                    link.download = `projekt-kostki-${new Date().toISOString().split('T')[0]}.jpg`
                    link.href = dataUrl
                    link.click()

                  } catch (error) {
                    console.error('Błąd:', error)
                    alert('Błąd podczas generowania obrazu')
                  } finally {
                    button.textContent = originalText || 'Pobierz projekt'
                    button.disabled = false
                  }
                }}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base text-white rounded-lg transition-colors font-sans font-medium touch-manipulation mobile-button"
                style={{ backgroundColor: '#1FA650' }}
              >
                Pobierz projekt
              </button>
              
              <button
                onClick={() => {
                  setUploadedImage(null)
                  setSurfaces([])
                  setSelectedSurface(null)
                  setPerspectivePoints(null)
                }}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base text-white rounded-lg transition-colors font-sans font-medium touch-manipulation mobile-button"
                style={{ backgroundColor: '#727271' }}
              >
                Wyczyść
              </button>
            </div>
            
            {/* Zaznacz powierzchnie section - tylko na mobile */}
            {uploadedImage && (
              <div className="pt-2 border-t border-gray-200 md:hidden">
                <h4 className="text-xs sm:text-sm font-semibold mb-2 sm:mb-3" style={{ color: '#3E3E3F' }}>Zaznacz powierzchnie</h4>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={startDrawingSurface}
                    disabled={isDrawing}
                    className="flex-1 px-3 sm:px-4 py-2 text-sm sm:text-base text-white rounded-lg transition-colors font-medium touch-manipulation mobile-button"
                    style={{ 
                      backgroundColor: isDrawing ? '#BFBFBF' : '#1FA650'
                    }}
                  >
                    {isDrawing ? `Rysowanie... (${currentPoints.length} punktów)` : 'Dodaj powierzchnię'}
                  </button>
                  
                  {isDrawing && (
                    <button
                      onClick={cancelDrawing}
                      className="px-3 sm:px-4 py-2 text-sm sm:text-base rounded-lg transition-colors font-medium touch-manipulation mobile-button"
                      style={{ 
                        backgroundColor: '#CE312A',
                        color: '#FFFFFF'
                      }}
                    >
                      Anuluj
                    </button>
                  )}
                  
                  <button
                    onClick={undoLastAction}
                    className="px-3 sm:px-4 py-2 text-sm sm:text-base rounded-lg transition-colors font-medium touch-manipulation mobile-button"
                    style={{ 
                      backgroundColor: '#EDEDED',
                      color: '#3E3E3F'
                    }}
                  >
                    Cofnij
                  </button>
                </div>
                <p className="text-xs sm:text-sm mt-2" style={{ color: '#727271' }}>
                  {isDrawing 
                    ? `Kliknij ${4 - currentPoints.length} punktów aby utworzyć powierzchnię`
                    : 'Kliknij 4 punkty narożne na zdjęciu'
                  }
                </p>
              </div>
            )}
            
            {/* Surfaces List - tylko na mobile */}
            {surfaces.length > 0 && (
              <div className="pt-2 border-t border-gray-200 md:hidden">
                <h4 className="text-xs sm:text-sm font-semibold mb-2 sm:mb-3" style={{ color: '#3E3E3F' }}>Powierzchnie ({surfaces.length})</h4>
                
                <div className="space-y-2">
                  {surfaces.map((surface) => (
                    <div
                      key={surface.id}
                      className={`p-2 sm:p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedSurface === surface.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{
                        borderColor: selectedSurface === surface.id ? '#08B3AA' : '#BFBFBF',
                        backgroundColor: selectedSurface === surface.id ? '#EDEDED' : '#FFFFFF'
                      }}
                      onClick={() => setSelectedSurface(surface.id)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium text-xs sm:text-sm" style={{ color: '#3E3E3F' }}>{surface.name}</div>
                          <div className="text-xs" style={{ color: '#727271' }}>{surface.tile ? '1 tekstura' : '0 tekstur'}</div>
                        </div>
                        <div className="flex space-x-1">
                          {surface.tile && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                clearSurfaceTexture(surface.id)
                              }}
                              className="transition-colors touch-manipulation mobile-button p-1"
                              style={{ color: '#E0590D' }}
                              title="Usuń teksturę"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              removeSurface(surface.id)
                            }}
                            className="transition-colors touch-manipulation mobile-button p-1"
                            style={{ color: '#BE2019' }}
                            title="Usuń powierzchnię"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Tile Selection - tylko na mobile */}
            {selectedSurface && (
              <div className="pt-2 border-t border-gray-200 md:hidden">
                <div className="flex justify-between items-center mb-2 sm:mb-3">
                  <h4 className="text-xs sm:text-sm font-semibold" style={{ color: '#3E3E3F' }}>Wybierz płytki</h4>
                  <div className="text-xs" style={{ color: '#727271' }}>
                    {surfaces.find(s => s.id === selectedSurface)?.tile ? '1 tekstura' : '0 tekstur'}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <button
                    onClick={() => setShowTilePopup(true)}
                    className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base text-white rounded-lg transition-colors font-medium touch-manipulation mobile-button"
                    style={{ backgroundColor: '#BE2019' }}
                  >
                    Wybierz
                  </button>
                </div>
              </div>
            )}
          </div>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="h-full bg-gray-200 overflow-hidden">
      {/* Mobile Navigation Bar */}
      <div className="md:hidden bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm">
        {/* Top row - Main navigation */}
        <div className="px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h1 className="font-sans text-lg font-semibold text-gray-900">Wizualizator</h1>
          </div>
          <div className="flex items-center space-x-2">
            {uploadedImage && (
              <>
                <div className="text-xs text-gray-600">
                  {surfaces.length} powierzchni
                </div>
                <button
                  onClick={() => setShowRightSidebar(!showRightSidebar)}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors touch-manipulation mobile-button relative"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Bottom row - Quick actions */}
        <div className="px-3 sm:px-4 pb-2 sm:pb-3">
          <div className="flex items-center justify-between space-x-3">
            {/* Upload Button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1.5 bg-red-600 text-white rounded-md text-xs font-medium hover:bg-red-700 transition-colors touch-manipulation mobile-button"
            >
              Zdjęcie
            </button>
            

          </div>
        </div>
      </div>

      {/* Main Content - Full Screen Layout */}
      <div className="flex h-[calc(100vh-112px)] md:h-[calc(100vh-64px)] relative overflow-auto">
        {/* Overlay dla mobilnych sidebarów - tylko prawy sidebar */}
        {showRightSidebar && typeof window !== 'undefined' && window.innerWidth < 768 && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden mobile-overlay"
            onClick={() => setShowRightSidebar(false)}
          />
        )}

        {/* Left Sidebar - ukryty na mobile */}
        <div className={`hidden md:block w-full sm:w-80 lg:w-96 bg-white border-r border-gray-200 fixed md:relative z-40 md:z-auto h-full shadow-lg overflow-y-auto`}>
            <div className="p-3 space-y-3">
            {/* Image Upload Section */}
            <div className="space-y-3">
              <h3 className="font-sans text-sm font-semibold" style={{ color: '#3E3E3F' }}>Zdjęcie działki/domu</h3>
              
              <div 
                className="border-2 border-dashed rounded-xl p-4 text-center transition-all duration-200"
                style={{ 
                  borderColor: '#BFBFBF',
                  backgroundColor: uploadedImage ? '#EDEDED' : '#FFFFFF'
                }}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {uploadedImage ? (
                  <div className="space-y-4">
                    <div className="relative">
                    <img 
                      src={uploadedImage} 
                      alt="Uploaded property" 
                        className="w-full h-32 object-cover rounded-lg shadow-sm"
                      />
                      <div className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-sm">
                        <svg className="w-4 h-4" style={{ color: '#1FA650' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setUploadedImage(null)
                        setSurfaces([])
                        setSelectedSurface(null)
                        setPerspectivePoints(null)
                      }}
                      className="px-4 py-2 rounded-lg transition-colors text-sm font-medium touch-manipulation mobile-button"
                      style={{ 
                        backgroundColor: '#CE312A',
                        color: '#FFFFFF'
                      }}
                    >
                      Usuń zdjęcie
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Przeciągnij zdjęcie lub kliknij aby wybrać</p>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG do 10MB</p>
                    </div>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                
                <div className="mt-4 space-y-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                    className="w-full px-4 py-3 text-white rounded-lg transition-colors text-sm font-medium touch-manipulation mobile-button"
                    style={{ 
                      backgroundColor: '#BE2019'
                    }}
                  >
                    {uploadedImage ? 'Zmień zdjęcie' : 'Wybierz z galerii'}
                  </button>
                  
                  <button
                    onClick={handleCameraCapture}
                    className="w-full px-4 py-3 rounded-lg transition-colors text-sm font-medium touch-manipulation mobile-button"
                    style={{ 
                      backgroundColor: '#EDEDED',
                      color: '#3E3E3F'
                    }}
                  >
                    Zrób zdjęcie aparatem
                </button>
                </div>
              </div>
            </div>

            {/* Surface Drawing Controls */}
            {uploadedImage && (
            <div className="space-y-3">
                <h3 className="text-sm font-semibold" style={{ color: '#3E3E3F' }}>Zaznacz powierzchnie</h3>
                

                
                <div className="space-y-3">
                  <button
                    onClick={startDrawingSurface}
                    disabled={isDrawing}
                    className="w-full px-3 py-2 text-white rounded-lg transition-colors text-sm font-medium touch-manipulation mobile-button"
                    style={{ 
                      backgroundColor: isDrawing ? '#BFBFBF' : '#1FA650'
                    }}
                  >
                    {isDrawing ? `Rysowanie... (${currentPoints.length} punktów)` : 'Dodaj powierzchnię'}
                  </button>
                  
                  
                  {isDrawing && (
                    <button
                      onClick={cancelDrawing}
                      className="w-full px-3 py-2 rounded-lg transition-colors text-sm font-medium touch-manipulation mobile-button"
                      style={{ 
                        backgroundColor: '#CE312A',
                        color: '#FFFFFF'
                      }}
                    >
                      Anuluj rysowanie
                    </button>
                  )}
                  
                  <button
                    onClick={undoLastAction}
                    className="w-full px-4 py-2 rounded-lg transition-colors text-sm font-medium touch-manipulation mobile-button"
                    style={{ 
                      backgroundColor: '#EDEDED',
                      color: '#3E3E3F'
                    }}
                  >
                    Cofnij ostatnią zmianę
                  </button>
                  
                  <p className="text-xs" style={{ color: '#727271' }}>
                    {isDrawing 
                      ? `Kliknij ${4 - currentPoints.length} punktów aby utworzyć powierzchnię`
                      : 'Kliknij 4 punkty narożne na zdjęciu'
                    }
                  </p>
                </div>


              </div>
            )}

            {/* Surfaces List */}
            {surfaces.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold" style={{ color: '#3E3E3F' }}>Powierzchnie ({surfaces.length})</h3>
                
                <div className="space-y-2">
                  {surfaces.map((surface) => (
                    <div
                      key={surface.id}
                      className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                        selectedSurface === surface.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{
                        borderColor: selectedSurface === surface.id ? '#08B3AA' : '#BFBFBF',
                        backgroundColor: selectedSurface === surface.id ? '#EDEDED' : '#FFFFFF'
                      }}
                      onClick={() => setSelectedSurface(surface.id)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium text-sm" style={{ color: '#3E3E3F' }}>{surface.name}</div>
                          <div className="text-xs" style={{ color: '#727271' }}>{surface.tile ? '1 tekstura' : '0 tekstur'}</div>
                        </div>
                        <div className="flex space-x-1">
                          {surface.tile && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                clearSurfaceTexture(surface.id)
                              }}
                              className="transition-colors touch-manipulation mobile-button p-1"
                              style={{ color: '#E0590D' }}
                              title="Usuń teksturę"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              removeSurface(surface.id)
                            }}
                            className="transition-colors touch-manipulation mobile-button p-1"
                            style={{ color: '#BE2019' }}
                            title="Usuń powierzchnię"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                        </div>
                  ))}
                </div>
                </div>
              )}

            {/* Tile Selection */}
            {selectedSurface && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold" style={{ color: '#3E3E3F' }}>Wybierz płytki</h3>
                  <div className="text-xs" style={{ color: '#727271' }}>
                    {surfaces.find(s => s.id === selectedSurface)?.tile ? '1 tekstura' : '0 tekstur'}
                  </div>
                </div>
                

                
                
                <div className="space-y-2">
                  <button
                    onClick={() => setShowTilePopup(true)}
                    className="w-full px-4 py-2 text-white rounded-lg transition-colors text-sm font-sans font-medium touch-manipulation mobile-button"
                    style={{ backgroundColor: '#BE2019' }}
                  >
                    Wybierz
                  </button>
                </div>
                
                    </div>
                  )}



          </div>
        </div>

        {/* Center - Visualization Canvas */}
        <div className="flex-1 bg-gray-50 relative min-h-[400px] overflow-y-auto">
          <div className="w-full p-3 sm:p-4 md:p-6 pb-32 md:pb-6">
            {renderVisualizationContent()}
          </div>
        </div>
                
        {/* Right Sidebar - Controls */}
        {uploadedImage && (
          <div className={`${showRightSidebar ? 'block' : 'hidden'} md:block w-full sm:w-80 lg:w-96 bg-white border-l border-gray-200 fixed md:relative z-40 md:z-auto h-full shadow-lg right-0 mobile-sidebar ${showRightSidebar ? 'open' : ''} overflow-y-auto`}>
            <div className="p-3 sm:p-4 pb-32 md:pb-4 space-y-3 sm:space-y-4">
              {/* Header - z przyciskiem zamknięcia na mobile */}
              <div className="border-b border-gray-200 pb-3 flex items-center justify-between">
                <div>
                  <h2 className="font-sans text-sm sm:text-base font-bold" style={{ color: '#3E3E3F' }}>Kontrolki</h2>
                  <p className="font-sans text-xs" style={{ color: '#727271' }}>Dostosuj wizualizację</p>
                </div>
                {/* Przycisk zamknięcia na mobile */}
                <button
                  onClick={() => setShowRightSidebar(false)}
                  className="lg:hidden p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors touch-manipulation mobile-button flex-shrink-0 ml-2"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Snap Controls - ukryte */}
              {/* <div className="space-y-3">
                <h3 className="text-sm font-semibold" style={{ color: '#3E3E3F' }}>Snap do siatki</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                        <input
                      type="checkbox"
                      checked={snapToGrid}
                      onChange={(e) => setSnapToGrid(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-sm text-gray-700">Włącz snap</span>
                      </div>
                      
                  {snapToGrid && (
                    <div className="space-y-2">
                      <label className="text-sm text-gray-600">Rozmiar siatki</label>
                      <input
                        type="range"
                        min="5"
                        max="50"
                        value={gridSize}
                        onChange={(e) => setGridSize(Number(e.target.value))}
                        className="w-full h-2 rounded-lg appearance-none cursor-pointer slider"
                        style={{
                          background: `linear-gradient(to right, #08B3AA 0%, #08B3AA ${((gridSize - 5) / (50 - 5)) * 100}%, #BFBFBF ${((gridSize - 5) / (50 - 5)) * 100}%, #BFBFBF 100%)`
                        }}
                      />
                    </div>
                  )}
                </div>
              </div> */}

              {/* Texture Controls */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold" style={{ color: '#3E3E3F' }}>Rozmiar bloków tekstury</h3>
                
                <div className="p-3 rounded-lg border" style={{ backgroundColor: '#EDEDED', borderColor: '#BFBFBF' }}>
                        <input
                    type="range"
                    min="10"
                    max="150"
                    value={tileSize}
                    onChange={(e) => setTileSize(Number(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer slider mobile-input"
                    style={{
                      background: `linear-gradient(to right, #E0590D 0%, #E0590D ${((tileSize - 10) / (150 - 10)) * 100}%, #BFBFBF ${((tileSize - 10) / (150 - 10)) * 100}%, #BFBFBF 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs mt-2" style={{ color: '#727271' }}>
                    <span>Mały</span>
                    <span className="font-semibold" style={{ color: '#E0590D' }}>Rozmiar</span>
                    <span>Duży</span>
            </div>
                  <div className="text-xs mt-1" style={{ color: '#727271' }}>
                    Im mniejszy rozmiar, tym więcej bloków na powierzchni
                    </div>
                  </div>
              </div>

              {/* Rotation Controls */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold" style={{ color: '#3E3E3F' }}>Obrót tekstury</h3>
                
                <div className="p-3 rounded-lg border" style={{ backgroundColor: '#EDEDED', borderColor: '#BFBFBF' }}>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={tileRotation}
                    onChange={(e) => setTileRotation(Number(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer slider mobile-input"
                              style={{
                      background: `linear-gradient(to right, #08B3AA 0%, #08B3AA ${(tileRotation / 360) * 100}%, #BFBFBF ${(tileRotation / 360) * 100}%, #BFBFBF 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs mt-2" style={{ color: '#727271' }}>
                    <span>0°</span>
                    <span className="font-semibold" style={{ color: '#08B3AA' }}>{tileRotation}°</span>
                    <span>360°</span>
                              </div>
                  <div className="text-xs mt-1" style={{ color: '#727271' }}>
                    Obróć teksturę dla lepszego dopasowania kierunku kostki
            </div>
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    <button
                      onClick={() => setTileRotation(0)}
                      className="px-3 py-2 text-xs bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors font-medium touch-manipulation mobile-button"
                    >
                      0°
                    </button>
                    <button
                      onClick={() => setTileRotation(90)}
                      className="px-3 py-2 text-xs bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors font-medium touch-manipulation mobile-button"
                    >
                      90°
                    </button>
                    <button
                      onClick={() => setTileRotation(180)}
                      className="px-3 py-2 text-xs bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors font-medium touch-manipulation mobile-button"
                    >
                      180°
                    </button>
                    <button
                      onClick={() => setTileRotation(270)}
                      className="px-3 py-2 text-xs bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors font-medium touch-manipulation mobile-button"
                    >
                      270°
                    </button>
                            </div>
                            </div>
                          </div>

            {/* Visualization Controls */}
            {surfaces.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold" style={{ color: '#3E3E3F' }}>Wizualizacja</h3>
                  
                  <div className="space-y-3">
                    <label className="text-sm" style={{ color: '#727271' }}>Widoczność zaznaczonych powierzchni:</label>
                  <input
                    type="range"
                    min="80"
                    max="100"
                    value={beforeAfterSlider}
                    onChange={(e) => setBeforeAfterSlider(Number(e.target.value))}
                      className="w-full h-2 rounded-lg appearance-none cursor-pointer slider mobile-input"
                      style={{
                        background: `linear-gradient(to right, #1FA650 0%, #1FA650 ${((beforeAfterSlider - 80) / (100 - 80)) * 100}%, #BFBFBF ${((beforeAfterSlider - 80) / (100 - 80)) * 100}%, #BFBFBF 100%)`
                      }}
                    />
                    <div className="flex justify-between text-xs" style={{ color: '#727271' }}>
                      <span>Min: 80%</span>
                      <span className="font-semibold" style={{ color: '#1FA650' }}>{beforeAfterSlider}%</span>
                      <span>Max: 100%</span>
                    </div>
                    </div>
                </div>
              )}

              {/* Perspective Controls */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold" style={{ color: '#3E3E3F' }}>Perspektywa</h3>
                
                <div className="space-y-3">
                  {!perspectivePoints ? (
                    <div className="space-y-3">
                      <div className="text-xs p-3 rounded-lg border" style={{ color: '#727271', backgroundColor: '#EDEDED', borderColor: '#BFBFBF' }}>
                        Punkty perspektywy zostaną automatycznie zainicjalizowane po załadowaniu obrazu
                      </div>
                  <button
                        onClick={initializePerspective}
                        className="w-full px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium touch-manipulation mobile-button"
                        style={{ backgroundColor: '#08B3AA' }}
                  >
                        Inicjalizuj teraz
                  </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="text-xs p-3 rounded-lg border" style={{ color: '#1FA650', backgroundColor: '#EDEDED', borderColor: '#BFBFBF' }}>
                        Punkty perspektywy aktywne - przeciągnij białe kwadraty w rogach
                      </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => {
                          // Automatycznie ustaw punkty na rogach wybranej powierzchni
                          if (selectedSurface) {
                            const surface = surfaces.find(s => s.id === selectedSurface)
                            if (surface && surface.points.length >= 4) {
                              setPerspectivePoints({
                                topLeft: surface.points[0],
                                topRight: surface.points[1],
                                bottomRight: surface.points[2],
                                bottomLeft: surface.points[3]
                              })
                            }
                          }
                        }}
                        className="px-3 py-2 text-white rounded-lg transition-colors text-xs font-medium touch-manipulation mobile-button"
                        style={{ backgroundColor: '#08B3AA' }}
                        disabled={!selectedSurface}
                      >
                        Automatycznie
                      </button>
                      
                      <button
                        onClick={() => {
                          // Reset perspektywy do domyślnej (środek obrazu)
                          const canvas = canvasRef.current
                          if (canvas) {
                            const centerX = canvas.width / 2
                            const centerY = canvas.height / 2
                            const offset = 150
                            setPerspectivePoints({
                              topLeft: { x: centerX - offset, y: centerY - offset },
                              topRight: { x: centerX + offset, y: centerY - offset },
                              bottomRight: { x: centerX + offset, y: centerY + offset },
                              bottomLeft: { x: centerX - offset, y: centerY + offset }
                            })
                          }
                        }}
                        className="px-3 py-2 text-white rounded-lg transition-colors text-xs font-medium touch-manipulation mobile-button"
                        style={{ backgroundColor: '#6B7280' }}
                      >
                        Reset
                      </button>
                    </div>

                    <button
                        onClick={() => setIsEditingPerspective(!isEditingPerspective)}
                        className="w-full px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium touch-manipulation mobile-button"
                        style={{ 
                          backgroundColor: isEditingPerspective ? '#BE2019' : '#08B3AA'
                        }}
                      >
                        {isEditingPerspective ? 'Ukryj punkty' : 'Pokaż punkty'}
                    </button>
                    </div>
                  )}
                </div>
              </div>
                </div>
              </div>
            )}

      </div>

      {/* Tile Selection Popup */}
      <TileSelectionPopup
        isOpen={showTilePopup}
        onClose={() => setShowTilePopup(false)}
        onTileSelect={handleTileSelect}
        selectedTile={selectedSurface ? surfaces.find(s => s.id === selectedSurface)?.tile || undefined : undefined}
      />
    </div>
  )
}

export default Wizualizator